import type { IntakeView, SignatureKind, SignedContent, CustomRequest, CustomRequestStatus, WorkshopLogo, BillingPeriod, LockedPrices, Plan, Pricing, SubscriptionState, TrialUsage, ReminderChannel, ReminderKind, ArticleInput, ArticleNumber, CustomerInput, ImportPlan, LegacyArticle, LegacyWorkOrderExtra, RequiredFieldChoices, Role, Row, VehicleInput, WorkOrderInput, WorkOrderLine, WorkOrderLineInput, WorkOrderStatus } from "@szerviz/core";

import type { CompanyCandidate, TaxpayerData } from "../../../../supabase/functions/_shared/taxpayer";
import type { FeedbackKind, FeedbackStatus, FeedbackVerdict } from "../../../../supabase/functions/_shared/feedback";
export type { CompanyCandidate, TaxpayerData };

export interface Customer extends CustomerInput {
  id: string;
  workshopId: string;
  createdAt: string;
  /** When the customer asked for reminders (set by the database; null: no consent). */
  reminderConsentAt?: string | null;
}

export interface Vehicle extends VehicleInput {
  id: string;
  workshopId: string;
  createdAt: string;
  /** Owner's name, filled by search for display. */
  customerName?: string | null;
  /** Engine node of the shared type tree (set only through setVehicleType). */
  typeId?: string | null;
}

/** What the AI read from a vehicle document photo; "unclear" fields must be checked by the user. */
export interface VehiclePhotoResult {
  documentKind: string;
  fields: Partial<Record<"plateNumber" | "vin" | "make" | "model" | "engineCode" | "powerKw" | "modelYear" | "fuelType" | "typeDescription" | "inspectionValidUntil", string | number>>;
  owner: { name: string; postalCode?: string; city?: string; addressLine?: string } | null;
  unclear: string[];
}

/** The business that concludes the subscription contract when the owner accepts the terms. */
export interface ContractDetails {
  companyName: string;
  address: string;
  taxNumber: string;
  regNumber?: string | null;
}

/** A concluded contract as the operator sees it. */
/** The owner's view of the custom adjustments of this year (Settings > Előfizetés). */
export interface MyCustomRequests {
  year: number;
  used: number;
  /** Included in the (effective) plan a year. */
  included: number;
  requests: CustomRequest[];
}

/** A custom request on the operator's workshop page. */
export interface OpCustomRequest extends CustomRequest {
  createdByEmail: string | null;
}

export interface NewCustomRequest {
  year: number;
  title: string;
  hours: number;
  included: boolean;
}
export type { CustomRequest, CustomRequestStatus };

export interface OpContract extends ContractDetails {
  version: string;
  userEmail: string | null;
  acceptedAt: string;
  confirmationSentAt: string | null;
}

/** A node of the shared type tree. */
export interface VehicleTypeNode {
  id: string;
  level: "make" | "model" | "engine";
  name: string;
}

/** An engine variant that fits the vehicle's own data. */
export interface VehicleTypeSuggestion {
  id: string;
  make: string;
  model: string;
  engine: string;
  score: number;
}

/** A tree node proposed from the data read from a registration certificate (an engine, or a model when no engine fits). */
export interface VehicleTypeMatch {
  id: string;
  level: "model" | "engine";
  make: string;
  model: string;
  engine: string | null;
  score: number;
}

/** A closed work order of the vehicle with its lines (the vehicle's own service history). */
export interface ServiceHistoryOrder {
  id: string;
  number: string;
  status: WorkOrderStatus;
  closedAt: string | null;
  odometerKm: number | null;
  items: WorkOrderItem[];
}

/** A fact of a type data sheet with the mechanics' votes. */
export interface VehicleFact {
  id: string;
  field: string;
  value: string;
  sourceUrl: string | null;
  origin: "ai" | "mechanic" | "import";
  confirms: number;
  rejects: number;
  /** Share of confirmations among the votes, null without votes. */
  percent: number | null;
  confirmed: boolean;
  myVote: boolean | null;
  /** A picture or document ("diagram"): a preview image on the source, never a copy (null: none). */
  previewUrl?: string | null;
}

export interface Workshop {
  id: string;
  name: string;
  language: string;
  currency: string;
  /** The workshop's choice of required fields (settings.requiredFields). */
  requiredFields: RequiredFieldChoices;
  /** General settings (settings.general): hourlyRateNet, defaultVatRate, workOrderPrefix. */
  general: Record<string, number | string | boolean | null>;
  /** Permission overrides (settings.permissions): permission → roles. */
  permissions: Record<string, Role[]>;
  /** Package of the workshop; only the operator changes it. */
  plan: Plan;
  /** Switched on features (settings.features), always within the package or the operator's grants. */
  features: string[];
  /** Features the operator allowed beyond the package (the workshop still switches them on). */
  extraFeatures?: string[];
  /** The workshop's own company logo (settings.logo), on its documents; null or missing: none. */
  logo?: WorkshopLogo | null;
}

/** A logo shrunk on the device, ready to upload (apps/desktop/src/logo.ts). */
export interface PreparedLogo {
  blob: Blob;
  width: number;
  height: number;
  type: "image/png" | "image/jpeg";
}

export interface WorkOrder extends WorkOrderInput {
  id: string;
  workshopId: string;
  number: string;
  status: WorkOrderStatus;
  receivedAt: string;
  readyAt: string | null;
  closedAt: string | null;
  needsReview: string | null;
  legacyId?: string | null;
  /** When the work order row last changed; a save checks it so a stale tab cannot overwrite newer data. */
  updatedAt?: string;
  /** Filled for lists and headers. */
  plateNumber?: string;
  plateCountry?: string | null;
  vehicleLabel?: string | null;
  customerName?: string | null;
  /** Gross total of the items. */
  gross?: number;
  /** Number of lines, and lines waiting for the customer's answer (lists, summary). */
  itemCount?: number;
  pendingApprovals?: number;
  /** Objections of the customer from the intake link that the workshop has not handled yet. */
  openObjections?: number;
  /** How the customer pays ("payments" feature); cash and card are paid at closing. */
  paymentMethod?: PaymentMethod | null;
  paidAt?: string | null;
}

export type PaymentMethod = "cash" | "card" | "transfer";
export const PAYMENT_METHODS: readonly PaymentMethod[] = ["cash", "card", "transfer"];

/** The invoice of a work order, issued through the invoicing provider. */
export interface Invoice {
  /** issued; pending: being issued now; unknown: no clear answer came, the owner resolves it */
  status: "issued" | "pending" | "unknown";
  number: string | null;
  gross: number | null;
  currency: string;
  viewUrl: string | null;
  issuedAt: string | null;
  /** Its cancellation: null none; pending: being cancelled now; unknown: no clear answer came, the owner resolves it. */
  cancelStatus?: "pending" | "unknown" | null;
  cancelReason?: string | null;
}

/** An invoice cancelled with a cancelling invoice (sztornó számla); kept as history. */
export interface CancelledInvoice {
  number: string | null;
  gross: number | null;
  currency: string;
  issuedAt: string | null;
  cancelNumber: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export interface WorkOrderItem extends WorkOrderLine {
  id: string;
  workOrderId: string;
  sortOrder: number;
}

export interface WorkOrderEvent {
  kind: "created" | "open" | "ready" | "closed" | "reopened" | "quote_sent" | "quote_answered";
  reason: string | null;
  at: string;
}

export interface WorkOrderDetail {
  order: WorkOrder;
  items: WorkOrderItem[];
  events: WorkOrderEvent[];
  /** Questions to the customer about lines, newest first. */
  quotes: QuoteRequest[];
}

/** One question to the customer: by link (answered on the phone page) or a yes recorded by phone. */
export type QuoteChannel = "email" | "sms";

export type BillingStatus = "trial" | "active" | "overdue" | "suspended" | "free";
export const BILLING_STATUSES: readonly BillingStatus[] = ["trial", "active", "overdue", "suspended", "free"];

export interface OpBilling {
  status: BillingStatus;
  /** Net, smallest unit of the currency. */
  monthlyFee: number | null;
  /** YYYY-MM-DD */
  paidUntil: string | null;
  note: string | null;
}

export interface OpWorkshop extends OpBilling {
  id: string;
  name: string;
  plan: Plan;
  features: string[];
  createdAt: string;
  ownerEmail: string | null;
  memberCount: number;
  workOrderCount: number;
  lastActivity: string | null;
  termsAccepted: boolean;
  smsCount: number;
  smsAmount: number;
  /** Discount on the monthly fee (percent), null = none. */
  discountPct: number | null;
  /** Own monthly AI allowance (forint), null = the package's. */
  aiAllowance: number | null;
  /** Operator labels (VIP, teszt, partner ...), never shown to the workshop. */
  tags: string[];
  /** Features allowed beyond the package. */
  featureGrants: string[];
  /** AI gift credit given this month and in all (forint). */
  giftMonthHuf: number;
  giftTotalHuf: number;
  /** Latest sign in of any member. */
  lastSignIn: string | null;
}

/** Subscription fields the operator changes; only the given ones change (null clears). */
export interface OpBillingPatch {
  status?: BillingStatus;
  monthlyFee?: number | null;
  discountPct?: number | null;
  paidUntil?: string | null;
  note?: string | null;
  aiAllowance?: number | null;
  tags?: string[];
  /** The workshop's own daily cap of the real AI cost (null: the platform's workshopDailyCapHuf). */
  aiDailyCapHuf?: number | null;
  /** The workshop's own monthly limit of extra credit purchases (null: monthlyPurchaseLimitHuf). */
  aiPurchaseLimitHuf?: number | null;
}

/** The workshop's own subscription (my_subscription): its state, the price list and this month's usage. */
export interface MySubscription extends SubscriptionState {
  /** Today in Budapest (the database's day). */
  today: string;
  /** The plan that works now (Profi in the first week of the trial, then the stored plan; see effectivePlan). */
  effectivePlan: Plan;
  /** The trial ended without a chosen plan: everything visible, nothing written until a plan is chosen. */
  readOnly: boolean;
  /** What the workshop used in the trial that Alap does not have (null outside the trial). */
  trialUsage: TrialUsage | null;
  pricing: Pricing;
  /** Users and monthly AI credit (forint) included in each plan. */
  seats: Record<Plan, number>;
  aiCredit: Record<Plan, number>;
  seatsUsed: number;
  seatLimit: number;
  /** This month's SMS (net) and bought AI credit (gross), billed with the subscription. */
  smsNet: number;
  aiCreditGross: number;
}

/** The owner's choices: the plan (only during the trial), the period, the extra users. */
export interface SubscriptionPatch {
  plan?: Plan;
  period?: BillingPeriod;
  extraSeats?: number;
}

/** Subscription fields of a workshop for the operator (op_subscriptions). */
export interface OpSubscription {
  workshopId: string;
  effectivePlan: Plan;
  readOnly: boolean;
  trialUntil: string | null;
  planAfterTrial: Plan | null;
  period: BillingPeriod;
  extraSeats: number;
  billingAnchor: string | null;
  founder: boolean;
  founderUntil: string | null;
  priceLockUntil: string | null;
  lockedPrices: LockedPrices | null;
}

/** Subscription fields the operator changes; only the given ones change. */
export interface OpSubscriptionPatch {
  trialUntil?: string | null;
  period?: BillingPeriod;
  extraSeats?: number;
  /** true: founder from the first paid day with the price list's months; false: not a founder. */
  founder?: boolean;
  founderUntil?: string | null;
}

/** A member as the operator sees it (support). */
export interface OpMember {
  userId: string;
  email: string;
  role: Role;
  createdAt: string | null;
  lastSignIn: string | null;
  /** Has an authenticator app set up (undefined: not known, e.g. an older database). */
  mfa?: boolean;
}

/** One action on many workshops at once (op_bulk). */
export type OpBulkAction =
  | { action: "plan"; value: { plan: Plan } }
  | { action: "feature"; value: { key: string; on: boolean } }
  | { action: "status"; value: { status: BillingStatus } }
  | { action: "extend"; value: { months: number } }
  | { action: "fee"; value: { monthlyFee: number | null } }
  | { action: "gift"; value: { amount: number; reason: string } }
  | { action: "tagAdd"; value: { tag: string } }
  | { action: "tagRemove"; value: { tag: string } };
export const OP_BULK_ACTIONS = ["plan", "feature", "status", "extend", "fee", "gift", "tagAdd", "tagRemove"] as const;

/** AI credit of a workshop (forint): monthly allowance first, then the bought credit. */
export interface AiCredit {
  allowance: number;
  usedThisMonth: number;
  boughtLeft: number;
  available: number;
  /** Extra packs that can be bought. */
  packs: number[];
}

/** Platform wide AI rules, set by the operator. */
export interface AiSettings {
  /** Credit spent (gross) = real cost × markup × (1 + VAT). */
  markup: number;
  /** VAT percent added on top of the marked up price (VMS-KER pays it; 27). */
  vatRate: number;
  /** Forint per US dollar. */
  usdHuf: number;
  /** Monthly credit included in each package (forint). */
  allowance: Record<Plan, number>;
  packs: number[];
  /** Warn when the real AI cost of a month passes this share of the monthly fee. */
  alertShare: number;
  /** Everyday AI help that does not use the credit (e.g. "vehiclePhoto", "dictation")... */
  freeKinds: string[];
  /** ...up to this many calls per workshop and month (old flat number; freeCalls by plan comes first). */
  freeMonthlyCalls: number;
  /** Cost safeguards (2026-11-03; packages/core aiLimits.ts). Missing on an older database. */
  dailyCostCapHuf?: number;
  workshopDailyCapHuf?: number;
  freeCalls?: Record<Plan, number>;
  trialAllowance?: number;
  trialFreeCalls?: number;
  trialSheetFills?: number;
  rateLimitPerMinute?: number;
  rateLimitPerDay?: number;
  monthlyPurchaseLimitHuf?: number;
  /** When usdHuf was last changed (set by the database). */
  usdHufChangedAt?: string | null;
}

/** Today's platform AI cost against the daily cap (op_ai_today). */
export interface OpAiToday {
  day: string;
  costHuf: number;
  calls: number;
  capHuf: number;
  /** When the operator's e-mail went out today (null: not yet). */
  alertAt: string | null;
}

/** A workshop's own AI limits (null: the platform's), today's real cost and this month's purchases. */
export interface OpAiLimit {
  workshopId: string;
  dailyCapHuf: number | null;
  purchaseLimitHuf: number | null;
  todayCostHuf: number;
  monthBoughtHuf: number;
}

/** A workshop's AI figures in a month (forint). */
export interface OpAiUsage {
  workshopId: string;
  calls: number;
  /** Real cost of the workshop's own calls. */
  costHuf: number;
  /** Credit it spent. */
  chargedHuf: number;
  /** Real cost of shared work started from this workshop (platform cost). */
  sharedCostHuf: number;
  /** Extra credit bought this month (goes on the bill). */
  boughtHuf: number;
}

/** What a workshop did in the last 7 days (next to the operator's trial extension). */
export interface OpTrialActivity {
  workOrders: number;
  vehicles: number;
  /** Members signed in during the last 7 days. */
  signedIn: number;
  lastActivity: string | null;
  lastSignIn: string | null;
}

export interface OpActivity {
  workshopId: string;
  workOrders: number;
  vehicles: number;
  members: number;
  seatLimit: number;
}

/**
 * Operator statistics of a period: what was done in the program (imported work orders, vehicles
 * and customers are left out). Money in minor units; revenue is the gross of the work orders
 * closed in the period without declined lines.
 */
export const OP_STAT_FIGURES = [
  "users", "activeUsers", "workOrdersCreated", "workOrdersClosed", "revenue", "invoicesIssued",
  "quotesSent", "quotesAnswered", "quoteItems", "quoteItemsApproved",
  "aiCalls", "aiCost", "aiSharedCost", "aiCharged", "aiMargin", "packsBought", "packsHuf",
  "smsCount", "smsAmount", "vehiclesNew", "customersNew", "knowledgeVotes",
] as const;
export type OpStatFigure = (typeof OP_STAT_FIGURES)[number];
export type OpStatFigures = Record<OpStatFigure, number>;

/** Platform totals of one period (forint total: HUF workshops only). */
export const OP_STAT_PLATFORM = ["workshopsTotal", "workshopsActive", "workshopsNew", "knowledgeTypes", "knowledgeFacts", "knowledgeConfirmed"] as const;
export type OpStatTotals = OpStatFigures & Record<(typeof OP_STAT_PLATFORM)[number], number> & { from: string; to: string };

/** The chosen period and the previous one of equal length. */
export interface OpStatsOverview {
  current: OpStatTotals;
  previous: OpStatTotals;
}

/** One workshop's figures of the period, with package, subscription and seats. */
export interface OpStatsWorkshop extends OpStatFigures {
  workshopId: string;
  name: string;
  plan: Plan;
  currency: string;
  createdAt: string;
  ownerEmail: string | null;
  billingStatus: BillingStatus;
  monthlyFee: number | null;
  seatsUsed: number;
  seatLimit: number;
  lastActivity: string | null;
}

export const OP_STAT_MONTH = ["workOrders", "closed", "revenue", "aiCalls", "aiCost", "aiSharedCost", "aiCharged", "smsCount", "smsAmount", "activeUsers"] as const;
/** One month of the series; month is YYYY-MM-01. */
export type OpStatsMonth = Record<(typeof OP_STAT_MONTH)[number], number> & { month: string };

export interface OpLogEntry {
  at: string;
  adminEmail: string | null;
  action: string;
  details: unknown;
}

/** A reminder that falls due soon ("reminders" feature), with what the program would do. */
export interface UpcomingReminder {
  vehicleId: string;
  plateNumber: string;
  plateCountry: string | null;
  vehicleLabel: string | null;
  customerId: string | null;
  customerName: string | null;
  hasEmail: boolean;
  hasPhone: boolean;
  /** The customer asked for reminders. */
  consent: boolean;
  kind: ReminderKind;
  /** YYYY-MM-DD */
  dueDate: string;
  dueKm: number | null;
  /** The day it goes out by itself. */
  sendFrom: string;
  /** Channels it would go on now (consented, used by the workshop, contact there, not sent yet). */
  channels: ReminderChannel[];
  /** Channels it already went on for this due date. */
  sentChannels: ReminderChannel[];
}

export interface SentReminder {
  id: string;
  vehicleId: string;
  plateNumber: string;
  plateCountry: string | null;
  kind: ReminderKind;
  dueDate: string;
  channel: ReminderChannel;
  /** E-mail address or the last digits of the number. */
  recipient: string | null;
  manual: boolean;
  sentAt: string;
}

/** Where a message went and what the SMS cost the workshop (minor units; 0 = free). */
export interface SentMessage {
  to: string;
  price: number;
}

export interface SmsUsage {
  count: number;
  paidCount: number;
  amount: number;
  currency: string;
}

/** Texts of a quote message, written by the program in the workshop's language. */
/** A line the customer declined earlier, still recommended for the vehicle. */
export interface Recommendation {
  itemId: string;
  workOrderId: string;
  workOrderNumber: string;
  declinedOn: string;
  line: WorkOrderLineInput;
  gross: number;
}

export interface QuoteMessage {
  subject: string;
  text: string;
  link: string;
  buttonLabel: string;
}

export interface QuoteRequest {
  id: string;
  token: string;
  channel: "link" | "phone";
  itemIds: string[];
  createdAt: string;
  expiresAt: string;
  answeredAt: string | null;
  approvedIds: string[];
  note: string | null;
}

/** A customer answered a quote on the link: shown to the workshop as a gentle notice. */
export interface QuoteNotice {
  quoteId: string;
  workOrderId: string;
  number: string;
  plateNumber: string | null;
  plateCountry: string | null;
  customerName: string | null;
  answeredAt: string;
  approved: number;
  total: number;
}

/** What the customer sees behind the link. */
export interface PublicQuote {
  workshopName: string;
  customerName: string | null;
  workshopPhone: string | null;
  workshopAddress: string | null;
  language: string;
  currency: string;
  number: string;
  plateNumber: string | null;
  plateCountry: string | null;
  vehicle: string | null;
  /** The discovered fault of the work order; null when none was written. */
  findings?: string | null;
  answeredAt: string | null;
  approvedIds: string[];
  expiresAt: string;
  items: { id: string; kind: WorkOrderItem["kind"]; description: string; quantity: number; unit: string | null; gross: number; optional: boolean }[];
}

/** A line used before, offered when typing a new one (with its last price). */
export type ItemSuggestion = WorkOrderLineInput;

export interface Article extends ArticleInput {
  id: string;
  workshopId: string;
  stock: number;
  usageCount: number;
  lastUsed: string | null;
  legacyId: string | null;
  /** Other numbers (OE, other brands, EAN); filled by getArticle. */
  numbers?: ArticleNumberRow[];
}

export interface ArticleNumberRow extends ArticleNumber {
  id: string;
}

export type StockMovementKind = "receipt" | "work_order" | "adjustment" | "opening";

export interface StockMovement {
  id: string;
  articleId: string;
  quantity: number;
  kind: StockMovementKind;
  unitCost: number | null;
  supplier: string | null;
  documentNo: string | null;
  note: string | null;
  workOrderNumber: string | null;
  createdAt: string;
}

export interface NewStockMovement {
  articleId: string;
  quantity: number;
  kind: "receipt" | "adjustment";
  unitCost?: number | null;
  supplier?: string | null;
  documentNo?: string | null;
  note?: string | null;
}

export type PhotoKind = "vehicle" | "damage" | "part" | "document" | "other" | "intake";
/** The kinds a user picks in the photo section; "intake" photos come from the guided intake capture. */
export const PHOTO_KINDS: readonly PhotoKind[] = ["vehicle", "damage", "part", "document", "other"];

export interface Photo {
  id: string;
  workshopId: string;
  vehicleId: string | null;
  workOrderId: string | null;
  kind: PhotoKind;
  /** Address for showing the image (valid for about an hour in the cloud). */
  url: string;
  width: number | null;
  height: number | null;
  caption: string | null;
  takenBy: string | null;
  createdAt: string;
  /** The view of an intake photo (kind "intake"), null otherwise. */
  intakeView: IntakeView | null;
}

export interface NewPhoto {
  vehicleId: string | null;
  workOrderId: string | null;
  kind: PhotoKind;
  caption?: string | null;
  intakeView?: IntakeView | null;
}

/** A signature given on the phone (feature "phoneSignature"): at intake or at handover. */
export interface WorkOrderSignature {
  id: string;
  workOrderId: string;
  kind: SignatureKind;
  signerName: string;
  /** Server time of signing. */
  signedAt: string;
  /** SHA-256 of the canonical content (packages/core contentHash). */
  contentHash: string;
  content: SignedContent;
  /** The signature image (object or data URL, does not expire). */
  imageUrl: string;
}

export interface NewSignature {
  kind: SignatureKind;
  signerName: string;
  /** PNG of the signature. */
  image: Blob;
  content: SignedContent;
  contentHash: string;
  userAgent: string | null;
}

/** The customer's personal intake link (feature "intakePhotos"). */
export interface IntakeLink {
  token: string;
  createdAt: string;
  expiresAt: string;
  sentTo: string | null;
  sentAt: string | null;
}

/** "Nem értek egyet" from the customer's intake link. */
export interface IntakeObjection {
  id: string;
  workOrderId: string;
  text: string;
  createdAt: string;
  resolvedAt: string | null;
}

export interface IntakeObjectionNotice extends IntakeObjection {
  number: string;
  plateNumber: string | null;
  plateCountry: string | null;
  customerName: string | null;
}

/** What the customer's intake page shows (no sign in). */
export interface PublicIntake {
  workshopName: string;
  workshopPhone: string | null;
  workshopAddress: string | null;
  language: string;
  logoUrl: string | null;
  number: string;
  plateNumber: string | null;
  plateCountry: string | null;
  vehicle: string | null;
  customerName: string | null;
  odometerKm: number | null;
  receivedAt: string;
  expectedReadyAt: string | null;
  customerRequest: string | null;
  expiresAt: string;
  photos: { id: string; view: IntakeView; createdAt: string; url: string }[];
  signature: { signedAt: string; signerName: string; contentHash: string; url: string | null } | null;
  objection: { createdAt: string } | null;
}

export interface WorkOrderImportResult {
  workOrders: number;
  items: number;
  skippedOrders: number;
  skippedItems: number;
  toReview: number;
}

/** Per-user preferences kept with the account. */
export interface UserPrefs {
  helpEnabled: boolean;
  /** When the help was first shown (ISO time); after about a week the program asks once whether to turn it off. */
  helpSince: string | null;
  helpAsked: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
}

/** Two-step sign in of the signed in user. */
export interface MfaStatus {
  /** An authenticator app (TOTP) is set up and confirmed. */
  factor: boolean;
  /** This session passed the second step (Supabase Auth: aal2). */
  verified: boolean;
}

/** Whether the signed in user must use two-step sign in (over all of their workshops). */
export interface MfaRequirement {
  required: boolean;
  /** Why: the operator, a workshop owner, or a manager where the owner requires it. */
  reason: "operator" | "owner" | "manager" | null;
  /** It would be required, but only in test workshops (operator tag "teszt"): it is off, a note says so. */
  testExempt: boolean;
}

/** A new, not yet confirmed authenticator: shown as a QR code (the uri) and as text (the secret). */
export interface MfaEnrollment {
  factorId: string;
  secret: string;
  /** otpauth://totp/... */
  uri: string;
}

export interface Member {
  userId: string;
  email: string;
  role: Role;
}

/** Everything of a workshop for the full data export. */
export interface ExportBundle {
  /** Table name → rows as stored (column names as in the database). */
  tables: Record<string, Record<string, unknown>[]>;
  /** Photo files: name inside the export and how to fetch the image. */
  photos: { name: string; load: () => Promise<Blob> }[];
}

/** What the user arrived for through an email link. */
export type AuthIntent = "none" | "invite" | "recovery";

export interface ImportProgress {
  phase: "customers" | "vehicles" | "workOrders" | "extras" | "articles" | "done";
  done: number;
  total: number;
}

export interface ImportResult {
  customers: number;
  vehicles: number;
  vehiclesWithoutOwner: number;
}

/**
 * Everything the user interface needs from the backend. Implemented by the
 * Supabase cloud store and by an in-memory store (demo mode and tests).
 */
export interface DataStore {
  readonly mode: "cloud" | "demo";

  currentUser(): Promise<SessionUser | null>;
  onAuthChange(listener: () => void): () => void;
  signIn(email: string, password: string): Promise<void>;
  /**
   * Returns true if the user must confirm the email before signing in.
   * language: the UI language, stored on the account so the Auth emails use it.
   */
  signUp(email: string, password: string, language?: string): Promise<{ needsConfirmation: boolean }>;
  signOut(): Promise<void>;
  /** Sends a password reset email (always resolves, so it does not reveal whether the address exists). */
  requestPasswordReset(email: string): Promise<void>;
  /** Sets the signed in user's password (after an invitation or a reset link, or to change it). */
  setPassword(password: string): Promise<void>;

  // ---- two-step sign in (authenticator app, TOTP) ----
  mfaStatus(): Promise<MfaStatus>;
  mfaRequirement(): Promise<MfaRequirement>;
  /** Starts setting up an authenticator (an unfinished earlier attempt is dropped). */
  mfaEnroll(): Promise<MfaEnrollment>;
  /** Confirms the new authenticator with its first 6 digit code; returns the 10 new one-time backup codes (shown once). */
  mfaConfirmEnroll(factorId: string, code: string): Promise<string[]>;
  /** The second step of the sign in (MFA_CODE_INVALID when wrong). */
  mfaVerify(code: string): Promise<void>;
  /**
   * Lost phone: one backup code removes the authenticator and signs the user out; after the next
   * sign in with the password the new phone is set up.
   */
  mfaRecover(code: string): Promise<void>;
  /** Ten new backup codes (the old ones stop working); needs the second step done. */
  mfaNewBackupCodes(): Promise<string[]>;
  /** How many unused backup codes are left. */
  mfaBackupCodesLeft(): Promise<number>;
  /** Switches it off (offered only where it is not required). */
  mfaDisable(): Promise<void>;
  /** Whether new users may sign up themselves (off in production: users are invited). */
  readonly allowSignUp: boolean;

  getPrefs(): Promise<UserPrefs>;
  savePrefs(patch: Partial<UserPrefs>): Promise<UserPrefs>;

  myWorkshops(): Promise<Workshop[]>;
  /** Legal documents the workshop has accepted (any version). */
  termsAcceptances(workshopId: string): Promise<{ document: string; version: string; acceptedAt: string }[]>;
  /** Records that the signed in user accepted the terms and the data processing agreement in this version. */
  /**
   * The owner accepts the terms in the name of the business (details recorded, unchangeable);
   * the contract confirmation e-mail with the accepted documents goes to the owner.
   */
  acceptTerms(workshopId: string, version: string, details?: ContractDetails): Promise<void>;
  /** Every table of the workshop and its photos (owner's full data export). */
  exportAll(workshopId: string): Promise<ExportBundle>;
  /** Owner and manager only (NOT_ALLOWED otherwise). */
  saveRequiredFields(workshopId: string, choices: RequiredFieldChoices): Promise<void>;
  /** Owner and manager only. */
  saveGeneralSettings(workshopId: string, values: Record<string, number | string | boolean | null>): Promise<void>;
  /** Owner only. */
  savePermissions(workshopId: string, permissions: Record<string, Role[]>): Promise<void>;
  /** Owner or manager switches features on and off (only those in the package). */
  saveFeatures(workshopId: string, features: string[]): Promise<void>;
  createWorkshop(name: string, language: string): Promise<Workshop>;

  listMembers(workshopId: string): Promise<Member[]>;
  /** Owner only. Returns "invited" for a new account, "added" for an existing one. */
  inviteMember(workshopId: string, email: string, role: Role): Promise<"invited" | "added">;
  updateMemberRole(workshopId: string, userId: string, role: Role): Promise<void>;
  removeMember(workshopId: string, userId: string): Promise<void>;

  searchCustomers(workshopId: string, query: string, limit?: number): Promise<Customer[]>;
  getCustomer(id: string): Promise<Customer | null>;
  saveCustomer(workshopId: string, input: CustomerInput, id?: string): Promise<Customer>;
  deleteCustomer(id: string): Promise<void>;

  searchVehicles(workshopId: string, query: string, limit?: number): Promise<Vehicle[]>;
  getVehicle(id: string): Promise<Vehicle | null>;
  vehiclesOfCustomer(customerId: string): Promise<Vehicle[]>;
  saveVehicle(workshopId: string, input: VehicleInput, id?: string): Promise<Vehicle>;
  deleteVehicle(id: string): Promise<void>;

  importLegacy(workshopId: string, plan: ImportPlan, onProgress?: (p: ImportProgress) => void): Promise<ImportResult>;
  /** Imports the previous program's closed work orders; customers and vehicles must be imported first. */
  importLegacyWorkOrders(workshopId: string, orderRows: Row[], itemRows: Row[], onProgress?: (p: ImportProgress) => void): Promise<WorkOrderImportResult>;
  /** Fills the odometer and the remark of imported work orders where they are empty; returns how many changed. */
  supplementLegacyWorkOrders(workshopId: string, rows: LegacyWorkOrderExtra[], onProgress?: (p: ImportProgress) => void): Promise<number>;

  /** Newest first. "active" = open and ready. */
  searchWorkOrders(workshopId: string, query: string, filter: "active" | "all", limit?: number): Promise<WorkOrder[]>;
  workOrdersOfVehicle(vehicleId: string): Promise<WorkOrder[]>;
  workOrdersOfCustomer(customerId: string): Promise<WorkOrder[]>;
  getWorkOrder(id: string): Promise<WorkOrderDetail | null>;
  createWorkOrder(workshopId: string, input: WorkOrderInput): Promise<WorkOrder>;
  /**
   * Saves the header. With `expectedUpdatedAt` the save only succeeds if nobody changed the work order since it was
   * loaded (else WORK_ORDER_CHANGED); returns the new change stamp.
   */
  updateWorkOrder(id: string, input: WorkOrderInput, expectedUpdatedAt?: string): Promise<string | undefined>;
  /** With "closed", the payment method when the "payments" feature is on. */
  setWorkOrderStatus(id: string, status: WorkOrderStatus, payment?: PaymentMethod): Promise<void>;
  /** A closed work order: the transfer arrived (paid true), or the method is corrected. */
  markWorkOrderPaid(id: string, method: PaymentMethod | null, paid: boolean): Promise<void>;
  invoiceOf(workOrderId: string): Promise<Invoice | null>;
  /** Issues the invoice through the provider (once per work order). */
  issueInvoice(workOrderId: string): Promise<Invoice>;
  /** An unknown invoice after looking at the provider: its number, or null if it was not issued. */
  resolveInvoice(workOrderId: string, number: string | null): Promise<void>;
  /** Owner or manager: cancels the current invoice with a cancelling invoice; a reason is required. Returns its number. */
  cancelInvoice(workOrderId: string, reason: string): Promise<{ number: string }>;
  /** An unknown cancellation after looking at the provider: the cancelling invoice's number, or null if it was not cancelled. */
  resolveInvoiceCancel(workOrderId: string, number: string | null): Promise<void>;
  /** The cancelled invoices of the work order, newest first. */
  cancelledInvoices(workOrderId: string): Promise<CancelledInvoice[]>;
  invoiceKeySet(workshopId: string): Promise<boolean>;
  /** Owner: the Számlázz.hu Agent key (empty clears it); never read back. */
  setInvoiceKey(workshopId: string, key: string): Promise<void>;
  reopenWorkOrder(id: string, reason: string): Promise<void>;
  deleteWorkOrder(id: string): Promise<void>;
  saveWorkOrderItem(workshopId: string, workOrderId: string, line: WorkOrderLineInput, id?: string): Promise<WorkOrderItem>;
  deleteWorkOrderItem(id: string): Promise<void>;
  /** Asks the customer about the pending lines; returns the secret token of the link. */
  createQuoteRequest(workOrderId: string): Promise<string>;
  /**
   * Sends the open quote link to the work order's customer from the server
   * (e-mail; SMS with the "sms" feature). Returns where it went.
   */
  sendQuote(workOrderId: string, channel: QuoteChannel, message: QuoteMessage): Promise<SentMessage>;
  /** Declined lines of the vehicle's work orders not followed up yet. */
  vehicleRecommendations(vehicleId: string): Promise<Recommendation[]>;
  /** Closes a recommendation: added to this open work order of the vehicle, or no longer relevant (null). */
  closeRecommendation(itemId: string, workOrderId: string | null): Promise<void>;
  /** E-mail to the customer listing the declined lines of the work order (feature "declinedWorkEmail"), once. */
  sendDeclinedWork(workOrderId: string, message: { subject: string; text: string }): Promise<SentMessage>;
  /** When the declined work e-mail of the work order went out (null: not yet). */
  declinedWorkSent(workOrderId: string): Promise<string | null>;
  /** Saves only the reminder dates of a vehicle (offered when a work order is closed). */
  saveVehicleDates(vehicleId: string, dates: Pick<VehicleInput, "inspectionValidUntil" | "nextServiceDate" | "nextServiceKm">): Promise<void>;
  /** What falls due within the days (default 60), soonest first ("reminders" feature). */
  upcomingReminders(workshopId: string, days?: number): Promise<UpcomingReminder[]>;
  /** The sent reminders, newest first. */
  sentReminders(workshopId: string): Promise<SentReminder[]>;
  /** "Send now": the reminder of one vehicle on every channel it may go on, once (REMINDER_NOTHING_TO_SEND otherwise). */
  sendReminderNow(vehicleId: string, kind: ReminderKind): Promise<{ sent: { channel: ReminderChannel; to: string }[]; price: number }>;
  /** A test SMS from Settings (owner or manager), the way the workshop sends SMS. */
  sendTestSms(workshopId: string, to: string, text: string): Promise<SentMessage>;
  /** This month's SMS: how many, how many paid, the amount (minor units). */
  smsUsage(workshopId: string): Promise<SmsUsage>;
  /** Whether the workshop's own Android phone is set up for SMS. */
  smsPhoneSet(workshopId: string): Promise<boolean>;
  /** Owner: a one-time code (30 minutes) for setting up the phone from the phone itself. */
  createSmsSetupToken(workshopId: string): Promise<string>;
  /** The phone's page (no sign in): the workshop's name, or null if the code is no longer valid. */
  smsSetupInfo(token: string): Promise<string | null>;
  /** The phone's page: saves the SMS app's cloud user name and password. */
  completeSmsSetup(token: string, user: string, password: string): Promise<void>;
  /** Owner: forgets the phone (e.g. it was replaced). */
  clearSmsPhone(workshopId: string): Promise<void>;

  // ---- shared type tree and data sheets (own vehicles only) ----
  vehicleTypeSuggest(vehicleId: string): Promise<VehicleTypeSuggestion[]>;
  /** Children of a tree node (the makes for null), for the type picker. */
  vehicleTypeChildren(parentId: string | null): Promise<VehicleTypeNode[]>;
  /** Tree nodes that fit data read from the registration certificate (or the make from the VIN); reads only. */
  vehicleTypeMatch(vehicleId: string, data: { make?: string | null; model?: string | null; engineCode?: string | null; powerKw?: number | null }): Promise<VehicleTypeMatch[]>;
  /** The vehicle's closed work orders with their lines, newest first (due services). */
  serviceHistory(vehicleId: string): Promise<ServiceHistoryOrder[]>;
  /** Puts the own vehicle into the tree: the given engine (or model) node, or a branch made from its own data. */
  setVehicleType(vehicleId: string, typeId?: string): Promise<string>;
  clearVehicleType(vehicleId: string): Promise<void>;
  vehicleTypePath(typeId: string): Promise<VehicleTypeNode[]>;
  vehicleDataSheet(vehicleId: string): Promise<VehicleFact[]>;
  addVehicleFact(vehicleId: string, field: string, value: string, sourceUrl?: string): Promise<void>;
  /** Confirms (true), rejects (false) or withdraws (null) the own vote. */
  voteVehicleFact(vehicleId: string, factId: string, agrees: boolean | null): Promise<void>;

  // ---- AI help for vehicles (only when the AI key is set on the server) ----
  aiAvailable(): Promise<boolean>;
  readVehiclePhoto(workshopId: string, imageBase64: string, mediaType: string): Promise<VehiclePhotoResult>;

  // ---- company data (NAV through the workshop's Számlázz.hu key; AI search for a name) ----
  /** NAV's data for a tax number (its first 8 digits), through the workshop's own Számla Agent key. */
  taxpayerLookup(workshopId: string, taxNumber: string): Promise<TaxpayerData>;
  /**
   * Tax number candidates for a company name (the owner on the registration certificate), at most 3:
   * found by an AI web search, checked with NAV when the key is set (verified ones carry NAV's data).
   */
  companyLookup(workshopId: string, name: string, city?: string | null): Promise<{ candidates: CompanyCandidate[]; keySet: boolean }>;
  /** The AI looks up the data sheet of the own vehicle's type (once per type). Returns how many facts it added. */
  fillDataSheet(vehicleId: string): Promise<number>;
  /** A mechanic's own question about the vehicle's technical data (AI credit); the rows are kept on the data sheet. */
  /** A quick lookup pack (AI_PACKS); free when the type already has it. */
  aiPack(vehicleId: string, pack: string): Promise<{ facts: { value: string; source: string | null }[]; charged: number; cached: boolean; knownSource?: boolean }>;
  /** Whether a pack lookup of the own vehicle starts from known sources (cheaper; the fuse sheet). */
  packKnownSource(vehicleId: string, pack: string): Promise<boolean>;
  askVehicleData(vehicleId: string, question: string): Promise<{ answer: string; facts: { value: string; source: string | null }[] }>;
  /** Whether the AI already looked up this vehicle's type. */
  typeFilled(vehicleId: string): Promise<boolean>;

  /** Users of the workshop and the seats its package allows. */
  seats(workshopId: string): Promise<{ used: number; limit: number }>;

  // ---- AI credit ----
  aiCredit(workshopId: string): Promise<AiCredit>;
  /** Roughly what a paid lookup costs from the AI credit (Ft), shown before tapping. */
  aiPriceHints(workshopId: string): Promise<AiPriceHints>;
  /** Owner or manager: buys one of the offered extra packs (added to the monthly bill). */
  buyAiCredit(workshopId: string, amount: number): Promise<void>;

  // ---- subscription (workshop side) ----
  /** The workshop's subscription status (notice when overdue, stop when suspended). */
  myBillingStatus(workshopId: string): Promise<BillingStatus>;
  /** Plan, trial, founder offer, period, extra users and the price list (any member reads it). */
  mySubscription(workshopId: string): Promise<MySubscription>;
  /** Owner only: plan after the trial (during the trial), period, extra users. */
  setSubscription(workshopId: string, patch: SubscriptionPatch): Promise<void>;
  /** Owner only: the custom adjustments of this year, used and included. */
  myCustomRequests(workshopId: string): Promise<MyCustomRequests>;

  // ---- operator console (Garagevo operator only) ----
  isPlatformAdmin(): Promise<boolean>;
  opWorkshops(): Promise<OpWorkshop[]>;
  opMembers(workshopId: string): Promise<{ email: string; role: Role }[]>;
  opSetPlan(workshopId: string, plan: Plan): Promise<void>;
  opSetFeatures(workshopId: string, features: string[]): Promise<void>;
  opSetBilling(workshopId: string, billing: OpBilling): Promise<void>;
  /** Changes only the given subscription fields (discount, own AI allowance, tags ...). */
  opUpdateBilling(workshopId: string, patch: OpBillingPatch): Promise<void>;
  /** Members with their last sign in. */
  opMemberList(workshopId: string): Promise<OpMember[]>;
  opSetMemberRole(workshopId: string, userId: string, role: Role): Promise<void>;
  /** Removes a member (never the last owner). */
  opRemoveMember(workshopId: string, userId: string): Promise<void>;
  /** Sends the password reset e-mail to a member and logs it. */
  opSendPasswordReset(workshopId: string, email: string): Promise<void>;
  /** Removes a member's authenticator (next sign in sets it up again), logs it and e-mails the owners. */
  opResetMfa(workshopId: string, userId: string): Promise<{ mailed: boolean }>;
  /** Features allowed beyond the package (the whole list). */
  opSetFeatureGrants(workshopId: string, keys: string[]): Promise<void>;
  /** One action on many workshops; returns the batch id of the log rows. */
  opBulk(workshopIds: string[], action: OpBulkAction): Promise<string>;
  opLog(workshopId: string): Promise<OpLogEntry[]>;
  /** Trial, founder offer, period and extra users of every workshop. */
  opSubscriptions(): Promise<OpSubscription[]>;
  opUpdateSubscription(workshopId: string, patch: OpSubscriptionPatch): Promise<void>;
  /** The price list (platform_settings 'pricing'). */
  opPricing(): Promise<Pricing>;
  opSetPricing(pricing: Pricing): Promise<void>;
  /** The AI figures of every workshop in a month (YYYY-MM-01; default this month). */
  opAiUsage(month?: string): Promise<OpAiUsage[]>;
  opAiToday(): Promise<OpAiToday>;
  opAiLimits(): Promise<OpAiLimit[]>;
  opAiSettings(): Promise<AiSettings>;
  opSetAiSettings(settings: AiSettings): Promise<void>;
  /** This month's work orders, new vehicles, members and seats of every workshop. */
  opActivity(): Promise<OpActivity[]>;
  /** Seats of one workshop; null = the package default. */
  opSetSeats(workshopId: string, maxUsers: number | null): Promise<void>;
  opContracts(workshopId: string): Promise<OpContract[]>;
  /** Extends the trial (in the trial, or read only after it) by 1 to 90 days from the later of its end and today; the new last day. */
  opExtendTrial(workshopId: string, days: number): Promise<string>;
  opTrialActivity(workshopId: string): Promise<OpTrialActivity>;
  /** Custom adjustments of a workshop (every year, newest first). */
  opCustomRequests(workshopId: string): Promise<OpCustomRequest[]>;
  /** Records one; an included one needs a free place in the year's allowance and at most 4 hours. */
  opAddCustomRequest(workshopId: string, r: NewCustomRequest): Promise<void>;
  /** Done, cancelled or open again, optionally with the actual hours. */
  opUpdateCustomRequest(id: string, status: CustomRequestStatus, hours?: number | null): Promise<void>;
  /** Platform totals of a period (YYYY-MM-DD, both days included) and of the previous period of equal length. */
  opStatsOverview(from: string, to: string): Promise<OpStatsOverview>;
  /** Every workshop's figures of the period. */
  opStatsWorkshops(from: string, to: string): Promise<OpStatsWorkshop[]>;
  /** The last months (current one last), platform wide or of one workshop. */
  opStatsMonthly(months: number, workshopId?: string | null): Promise<OpStatsMonth[]>;
  /** Records the customer's answer given by phone or in person (approved line ids, the rest is declined). */
  recordQuoteAnswer(workOrderId: string, approvedIds: string[], note?: string): Promise<void>;
  /** The customer's page (no sign in). */
  quoteView(token: string): Promise<PublicQuote>;
  quoteAnswer(token: string, approvedIds: string[]): Promise<void>;
  /** Answers the customers gave on their links after the given time, newest first. */
  recentQuoteAnswers(workshopId: string, sinceIso: string): Promise<QuoteNotice[]>;
  /** Lines used before whose description starts with or contains the text, newest price first. */
  itemSuggestions(workshopId: string, text: string, limit?: number): Promise<ItemSuggestion[]>;
  /** Highest odometer reading known for the vehicle (work orders and the vehicle itself). */
  lastOdometer(vehicleId: string): Promise<number | null>;

  /** Articles by part number (also cross numbers and EAN) or name; most used first. */
  searchArticles(workshopId: string, query: string, limit?: number): Promise<Article[]>;
  getArticle(id: string): Promise<Article | null>;
  saveArticle(workshopId: string, input: ArticleInput, id?: string): Promise<Article>;
  deleteArticle(id: string): Promise<void>;
  addArticleNumber(workshopId: string, articleId: string, n: ArticleNumber): Promise<ArticleNumberRow>;
  deleteArticleNumber(id: string): Promise<void>;
  stockMovements(articleId: string, limit?: number): Promise<StockMovement[]>;
  /** Goods receipt or stocktake correction (work order movements come from the lines). */
  addStockMovement(workshopId: string, m: NewStockMovement): Promise<void>;
  importLegacyArticles(workshopId: string, articles: LegacyArticle[], onProgress?: (p: ImportProgress) => void): Promise<number>;

  /** Photos of a work order, or of a vehicle (all of its work orders too), newest first. */
  listPhotos(filter: { workOrderId?: string; vehicleId?: string }): Promise<Photo[]>;
  /** Stores an already shrunk JPEG. */
  addPhoto(workshopId: string, photo: NewPhoto, image: { blob: Blob; width: number; height: number }): Promise<Photo>;
  deletePhoto(id: string): Promise<void>;

  // ---- intake with photos and the signature on the phone ----
  /** The phone signatures of a work order (intake, handover). */
  signatures(workOrderId: string): Promise<WorkOrderSignature[]>;
  /** Stores a signature once (image and record); SIGNATURE_EXISTS when that kind is already signed. */
  addSignature(workshopId: string, workOrderId: string, s: NewSignature): Promise<WorkOrderSignature>;
  /** A new personal intake link for the customer (an earlier one stops working); returns the token. */
  createIntakeLink(workOrderId: string): Promise<string>;
  /** The newest intake link of the work order (also an expired one), or null. */
  lastIntakeLink(workOrderId: string): Promise<IntakeLink | null>;
  /** E-mails the intake link to the work order's customer (the link must be the newest working one). */
  sendIntakeEmail(workOrderId: string, message: QuoteMessage): Promise<SentMessage>;
  /** The customer's intake page (no sign in). */
  intakeView(token: string): Promise<PublicIntake>;
  /** The customer's "Nem értek egyet" (no sign in; only without a phone signature, once per link). */
  intakeObject(token: string, text: string): Promise<void>;
  intakeObjections(workOrderId: string): Promise<IntakeObjection[]>;
  /** Objections that arrived after the given time, newest first (the corner notice). */
  recentIntakeObjections(workshopId: string, sinceIso: string): Promise<IntakeObjectionNotice[]>;
  /** The workshop dealt with the objection. */
  resolveIntakeObjection(id: string): Promise<void>;
  /** Uploads the workshop's logo (owner or manager), replaces the previous one and records it in settings.logo. */
  saveLogo(workshopId: string, image: PreparedLogo): Promise<WorkshopLogo>;
  /** Removes the workshop's logo: documents show the workshop's name only again. */
  removeLogo(workshopId: string): Promise<void>;
  /** A link to show or print the logo (object or data URL, does not expire); null when it cannot be read. */
  logoImageUrl(workshopId: string, logo: WorkshopLogo): Promise<string | null>;
  /** The logo of the workshop that sent this quote link, for the signed out quote page; null: none. */
  quoteLogoUrl(token: string): Promise<string | null>;

  // ---- feedback loop (GitHub issues through the "feedback" function) ----
  /** Sends a report (bug, idea or question) to the developer. */
  feedbackReport(workshopId: string, input: FeedbackInput): Promise<{ issueNumber: number }>;
  /** The workshop's reports, newest first, each with its conversation (oldest comment first). */
  feedbackList(workshopId: string): Promise<FeedbackReport[]>;
  /** The reporter's answer: checked and good (closes it), still wrong (reopens it, text required) or a comment. */
  feedbackReply(workshopId: string, issueNumber: number, verdict: FeedbackVerdict, text: string): Promise<void>;
}

export type { FeedbackKind, FeedbackStatus, FeedbackVerdict };

export interface FeedbackInput {
  kind: FeedbackKind;
  title: string;
  text: string;
  /** Hash route of the page the user came from ("#workOrders/<id>"). */
  page: string;
  appVersion: string;
  userAgent: string;
  /** Base64 JPEG (at most 1600 px), without the data: prefix. */
  screenshot?: string | null;
}

export interface FeedbackComment {
  author: string;
  /** Written by the developer (not by a reporter through the program). */
  fromDeveloper: boolean;
  body: string;
  createdAt: string;
}

export interface FeedbackReport {
  issueNumber: number;
  kind: FeedbackKind;
  title: string;
  text: string;
  createdAt: string;
  status: FeedbackStatus;
  comments: FeedbackComment[];
}

/** Rough credit (Ft) of a quick lookup, one that must find a diagram, and an own question. */
export interface AiPriceHints {
  dataPack: number;
  dataPackDiagram: number;
  /** A lookup that starts from known sources (the fuse sheet when the type or the make has them). */
  dataPackKnown: number;
  dataQuestion: number;
}
